import { useState } from "react";
import { post } from "aws-amplify/api";
import PatientSelectTypeahead from "./PatientSelectTypeahead";

// Simple 20ms gap helper, matching the throttle used elsewhere for PCC calls
const MIN_PCC_GAP_MS = 20;
let lastPccCallTime = 0;
async function ensureMinPccGap() {
  const now = Date.now();
  const elapsed = now - lastPccCallTime;
  const wait = Math.max(0, MIN_PCC_GAP_MS - elapsed);
  if (wait > 0) {
    await new Promise((resolve) => setTimeout(resolve, wait));
  }
  lastPccCallTime = Date.now();
}

// Backend returns one clinical item per line (see formatPccClinicalItems); render as a real list.
function renderClinicalItemList(text) {
  const items = String(text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  if (!items.length) {
    return <div className="text-muted">N/A</div>;
  }

  return (
    <ul className="brief-me-item-list">
      {items.map((item, idx) => (
        <li key={idx} className="text-muted">
          {item}
        </li>
      ))}
    </ul>
  );
}

/**
 * "Brief Me" — lets a PCC-connected provider look up a patient in PointClickCare
 * and review their PCC medications/conditions/diagnostic reports before starting
 * a visit/recording. Read-only; nothing here is persisted to a session.
 */
export default function BriefMe({
  orgUuid,
  pccUserId,
  token,
  facilitiesOptions = [],
  quotaExceeded,
  onClose,
}) {
  const [selectedFacilityId, setSelectedFacilityId] = useState("");
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [clinicalData, setClinicalData] = useState(null);
  const [fileLoadingReportId, setFileLoadingReportId] = useState(null);
  const [fileError, setFileError] = useState("");

  const handlePatientPicked = async (p) => {
    setPatient(p);
    setClinicalData(null);
    setError("");
    setWarning("");

    const patientId = p?.patientId ? String(p.patientId) : "";
    if (!patientId || quotaExceeded) return;

    setLoading(true);
    try {
      await ensureMinPccGap();

      const resp = await post({
        apiName: "pccapi",
        path: "/pushToPCC",
        options: {
          body: {
            action: "patientClinicalData",
            orgUuid,
            pccAccessToken: token,
            pccUserId,
            facilityId: selectedFacilityId ? Number(selectedFacilityId) : undefined,
            patientId,
          },
          headers: {
            "X-PCC-OrgUuid": orgUuid,
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      }).response;

      const raw = await resp.body.text();
      let data = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        data = null;
      }

      if (resp.statusCode < 200 || resp.statusCode >= 300) {
        throw new Error(data?.error || raw || "Failed to load patient clinical data.");
      }

      setClinicalData({
        medicationsText: data?.medicationsText || "",
        conditionsText: data?.conditionsText || "",
        diagnosticReportsText: data?.diagnosticReportsText || "",
        diagnosticReportItems: Array.isArray(data?.diagnosticReportItems)
          ? data.diagnosticReportItems
          : [],
      });

      const resourceErrors = Array.isArray(data?.clinicalDataErrors)
        ? data.clinicalDataErrors
        : [];
      if (resourceErrors.length) {
        setWarning(`PCC could not load: ${resourceErrors.map((item) => item.resource).join(", ")}.`);
      }
    } catch (err) {
      console.warn("Brief Me clinical data fetch failed:", err);
      setError("Could not load PCC medications, conditions/problems, or diagnostic reports for this patient.");
    } finally {
      setLoading(false);
    }
  };

  const handleViewReportFile = async (reportId) => {
    if (!reportId || quotaExceeded) return;

    setFileError("");
    setFileLoadingReportId(reportId);
    const reportWindow = window.open("about:blank", "_blank");

    if (!reportWindow) {
      setFileLoadingReportId(null);
      setFileError("Your browser blocked the report window. Allow pop-ups and try again.");
      return;
    }

    reportWindow.document.title = "Loading diagnostic report";
    reportWindow.document.body.textContent = "Loading diagnostic report…";

    try {
      await ensureMinPccGap();

      const resp = await post({
        apiName: "pccapi",
        path: "/pushToPCC",
        options: {
          body: {
            action: "diagnosticReportFile",
            orgUuid,
            pccAccessToken: token,
            reportId,
          },
          headers: {
            "X-PCC-OrgUuid": orgUuid,
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      }).response;

      const raw = await resp.body.text();
      let data = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        data = null;
      }

      if (resp.statusCode < 200 || resp.statusCode >= 300 || !data?.fileBase64) {
        throw new Error(data?.error || raw || "Failed to load report file.");
      }

      const byteChars = atob(data.fileBase64);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], {
        type: data.contentType || "application/octet-stream",
      });
      const blobUrl = URL.createObjectURL(blob);
      reportWindow.location.href = blobUrl;
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      console.warn("Brief Me report file fetch failed:", err);
      reportWindow.close();
      setFileError("Could not load the file attached to that diagnostic report.");
    } finally {
      setFileLoadingReportId(null);
    }
  };

  return (
    <div className="brief-me-overlay">
      <div className="brief-me-panel">
        <div className="brief-me-header">
          <h3>🔎 Brief Me</h3>
          <button onClick={onClose} className="button danger brief-me-close-button">
            Close
          </button>
        </div>
        <p className="text-muted">
          Look up a patient in PointClickCare to review their medications, conditions/problems, and
          diagnostic reports before you start the visit.
        </p>

        {quotaExceeded ? (
          <div className="text-danger">⛔ PCC quota reached, try again later.</div>
        ) : (
          <>
            <div className="field-group">
              <label className="field-label" htmlFor="brief-me-facility">
                Facility
              </label>
              <select
                id="brief-me-facility"
                value={selectedFacilityId}
                onChange={(e) => {
                  setSelectedFacilityId(e.target.value);
                  setPatient(null);
                  setClinicalData(null);
                }}
                disabled={facilitiesOptions.length === 0}
              >
                <option value="" disabled>
                  Select a facility…
                </option>
                {facilitiesOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field-group">
              <label className="field-label">Patient (search)</label>
              <PatientSelectTypeahead
                orgUuid={orgUuid}
                pccUserId={pccUserId}
                pccAccessToken={token}
                selectedFacilityId={selectedFacilityId}
                onSelect={handlePatientPicked}
                disabled={!selectedFacilityId}
              />
            </div>

            {patient && (
              <div className="text-muted" style={{ marginTop: 6 }}>
                Selected: <strong>{patient.name || "Unknown"}</strong>
                {patient.mrn ? ` — MRN ${patient.mrn}` : ""}
                {patient.dob ? ` — DOB ${patient.dob}` : ""}
              </div>
            )}

            {loading && (
              <div className="text-muted" style={{ marginTop: 6 }}>
                Loading PCC medications, conditions/problems, and diagnostic reports…
              </div>
            )}
            {error && (
              <div className="text-danger" style={{ marginTop: 6 }}>
                {error}
              </div>
            )}
            {warning && (
              <div className="text-muted" style={{ marginTop: 6 }}>
                {warning}
              </div>
            )}

            {clinicalData && (
              <div className="brief-me-results">
                <div className="field-group">
                  <label className="field-label">PCC Medications</label>
                  {renderClinicalItemList(clinicalData.medicationsText)}
                </div>
                <div className="field-group">
                  <label className="field-label">PCC Conditions / Problems</label>
                  {renderClinicalItemList(clinicalData.conditionsText)}
                </div>
                <div className="field-group">
                  <label className="field-label">PCC Diagnostic Reports</label>
                  {clinicalData.diagnosticReportItems.length ? (
                    <div className="brief-me-report-list">
                      {clinicalData.diagnosticReportItems.map((item, idx) => (
                        <div key={idx} className="brief-me-report-card">
                          <div className="brief-me-report-card-header">
                            <strong>{item.label}</strong>
                            {item.reportId && (
                              <button
                                type="button"
                                onClick={() => handleViewReportFile(item.reportId)}
                                className="button save"
                                disabled={fileLoadingReportId === item.reportId}
                              >
                                {fileLoadingReportId === item.reportId ? "Loading…" : "View File"}
                              </button>
                            )}
                          </div>
                          {item.details?.length > 0 && (
                            <dl className="brief-me-report-details">
                              {item.details.map((d) => (
                                <div key={d.key} className="brief-me-report-detail-row">
                                  <dt>{d.label}</dt>
                                  <dd>{d.value}</dd>
                                </div>
                              ))}
                            </dl>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-muted">N/A</div>
                  )}
                  {fileError && (
                    <div className="text-danger" style={{ marginTop: 6 }}>
                      {fileError}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
